/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState, DragEvent, useRef, ChangeEvent, MouseEvent } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { useDispatch, useSelector } from 'react-redux';
import piexifjs from 'piexifjs';

import CrossIcon from '../../public/icons/cross-icon.svg';
import DocIcon from '../../public/icons/doc.svg';
import { formatBytes } from '../../utils/helper';
import UploadJSON from '../../public/assets/upload.json';
import OrganisationService from '../../services/organisation/organisation.service';
import { RootState } from '../../store/reducers';
import { SHOW_SNACKBAR } from '../../store/types';
import ConversationsService from '../../services/conversations/conversations.service';
import FileUploadIcon from '../../public/icons/file-upload.svg';
import TinyTick from '../../public/icons/tiny-tick.svg';

import styles from './recording-upload.module.css';

type FileUploadProps = {
  accept?: string[];
  chunkSize?: number;
  maxSize: number;
  canCancelUpload: boolean;
  onFileUpload: () => void;
  setErrors?: (err: string) => void;
  shouldUpload?: boolean;
  onFileSave?: ({
    data,
    file_name,
  }: {
    data: string;
    file_name: string;
  }) => void;
  handleRecordingFile?: Function;
  handleUploadedFile?: Function;
};

type FileItem = {
  fileName: string;
  fileSize: number;
  progress: number;
  canCancel: boolean;
  setFile: Function;
  setSuccess: Function;
  handleRecordingFile: Function;
  handleUploadedFile: Function;
};

const FileUpload = (props: FileUploadProps): JSX.Element => {
  const {
    accept = [],
    chunkSize = 0,
    maxSize,
    canCancelUpload,
    onFileUpload,
    setErrors = () => {},
    shouldUpload = true,
    onFileSave = () => {},
    handleRecordingFile,
    handleUploadedFile,
  } = props;
  const [isInBounds, setIsInBounds] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  const dispatch = useDispatch();

  const { selectedWorkspace } = useSelector((state: RootState) => state.common);

  const handleShowSnackbar = (type, message) => {
    dispatch({
      type: SHOW_SNACKBAR,
      payload: {
        type,
        message,
      },
    });
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsInBounds(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsInBounds(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsInBounds(false);
    if (!e?.dataTransfer?.files?.length) return;
    setFile(e?.dataTransfer?.files?.[0]);
    setIsUploading(true);
    handleFileUpload(e?.dataTransfer?.files?.[0]);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!e?.target?.files?.length) return;
    setFile(e?.target?.files?.[0]);
    setIsUploading(true);
    handleFileUpload(e?.target?.files?.[0]);
  };

  const handleFileUpload = (_file: File) => {
    const isValid = validate(_file);
    if (isValid) {
      handleUploadedFile(_file);
      uploadFile(_file);
    }
  };

  const validate = (_file: File): boolean => {
    let errorCounter = 0;
    if (
      accept &&
      accept?.length &&
      !accept.includes(
        `.${
          _file?.name?.substring(
            _file?.name?.lastIndexOf('.') + 1,
            _file?.name?.length,
          ) || ''
        }`,
      )
    ) {
      errorCounter += 1;
      setError(
        `Document could not be uploaded. Please recheck the format and upload again.`,
      );
      setErrors(
        'Document could not be uploaded. Please recheck the format and upload again.',
      );
      setIsUploading(false);
    }

    if (_file?.size > maxSize) {
      errorCounter += 1;
      setError(`Document cannot be larger than ${formatBytes(maxSize)}`);
      setErrors(`Document cannot be larger than ${formatBytes(maxSize)}`);

      setIsUploading(false);
    }

    return errorCounter === 0;
  };

  const parseAndUploadFile = (_file: File) => {
    const chunkLength = chunkSize || maxSize;
    // const totalChunk = Math.ceil(_file.size / chunkLength);
    let from = 0;

    const updateProgress = (event: ProgressEvent<FileReader>) => {
      setProgress((event?.loaded / event?.total) * 100);
    };

    const uploadChunk = async (readerEvent: ProgressEvent<FileReader>) => {
      const fileChunk = readerEvent.target.result;
      const fileReadError = readerEvent.target.error;
      if (fileReadError) {
        setError(
          'Error in reading the file. Please recheck the file and upload again',
        );
        setErrors(
          'Error in reading the file. Please recheck the file and upload again',
        );
        setIsUploading(false);
        return;
      }
      if (fileChunk) {
        from += 1;
        // setProgress(Math.round((from / totalChunk) * 100));
        try {
          if (shouldUpload) {
            const r = fileChunk?.toString();
            handleRecordingFile(r?.split(',')[1]);
          }
        } catch (err) {
          setIsUploading(false);
          setProgress(0);
          if (err.response?.data.code === 'INTERNAL_SERVER_ERROR')
            handleShowSnackbar('error', err.response?.data.message);
          else {
            setError(err.response?.data.message);
            setErrors(err.response?.data.message);
          }
          return;
        }
      }
      if (from * chunkLength >= _file.size) {
        // API call (chunk upload complete)
        setError('');
        setErrors('');
        setSuccess('File uploaded successfully');
        setProgress(100);
        setIsUploading(false);
        onFileUpload();
        if (onFileSave && typeof onFileSave === 'function') {
          onFileSave({
            data: fileChunk.toString(),
            file_name: _file?.name,
          });
        }
        // setTimeout(() => {
        //   setFile(null);
        //   setSuccess('');
        // }, 1000);
        return;
      }
      readChunk(from * chunkLength);
    };

    const readChunk = (offset: number) => {
      const reader = new FileReader();
      const blob = _file.slice(offset, offset + chunkLength);
      reader.onload = uploadChunk;
      reader.onprogress = updateProgress;
      reader.readAsDataURL(blob);
    };

    readChunk(from);
  };

  const uploadFile = async (_file: File) => {
    try {
      setError('');
      setErrors('');
      setSuccess('');
      await parseAndUploadFile(_file);
    } catch (err) {
      handleShowSnackbar('error', err.response?.data.message);
    }
  };

  return (
    <div
      className={`${
        isInBounds
          ? 'border-solid bg-opacity-10 border-4'
          : 'border-dashed border bg-opacity-30'
      } bg-secondary-light bg-opacity-30 h-full w-full max-h-60  border-secondary rounded-lg flex flex-col items-center ${
        file ? 'justify-end' : 'justify-center'
      }  relative px-6 pb-2.5 pt-2.5`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => inputFileRef.current.click()}
    >
      <input
        type="file"
        ref={inputFileRef}
        className="hidden sr-only absolute top-0 left-0"
        onChange={handleInputChange}
        accept={accept.join(',')}
        onClick={() => {
          inputFileRef.current.setAttribute('value', null);
        }}
      />
      <div className="w-20 h-20 bg-secondary-light rounded-full mb-4 flex justify-center items-center relative">
        <FileUploadIcon />
      </div>
      {!isInBounds && !file && (
        <div className="flex flex-col items-center">
          <div className="mb-1 text-primary-dark text-xs lg:text-base leading-3 lg:leading-5 font-semibold">
            Drag & drop your file here or{' '}
            <a
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                inputFileRef.current.click();

                event.stopPropagation();
              }}
              className="font-normal text-secondary underline     cursor-pointer"
            >
              choose file
            </a>
          </div>
          <div className="text-tag-text-three flex flex-col items-center text-xs lg:text-sm leading-3 lg:leading-4 font-normal gap-1">
            <div>File Supported: {accept.join(', ') || []}</div>
            <div>Max size: {formatBytes(maxSize)}</div>
          </div>
        </div>
      )}
      {file && (
        <FileItemComponent
          fileName={file?.name}
          fileSize={file?.size}
          progress={progress}
          canCancel={canCancelUpload}
          setFile={setFile}
          setSuccess={setSuccess}
          handleRecordingFile={handleRecordingFile}
          handleUploadedFile={handleUploadedFile}
        />
      )}
      {success && (
        <div className="text-xs leading-3 text-positive-green mt-1.5 w-full flex">
          <TinyTick /> <span className="mx-1 text-primary-grey">{success}</span>
        </div>
      )}
      <div className="text-xs leading-3 text-negative-red mt-1.5 w-full">
        {error || ''}
      </div>
    </div>
  );
};

export const FileItemComponent = (props: FileItem): JSX.Element => {
  const {
    fileName,
    fileSize,
    progress,
    canCancel,
    setFile,
    setSuccess,
    handleRecordingFile,
    handleUploadedFile,
  } = props;
  return (
    <div className="border border-audio-timeline p-4 w-full rounded-lg flex justify-between mt-4 bg-white">
      <div className="flex gap-3 items-center">
        <DocIcon className="w-6 h-6 text-secondary" />
        <div className="flex flex-col gap-1.5">
          <div className="text-xs leading-3">{fileName}</div>
          <div className="text-xs leading-3 font-normal text-tag-text-three">
            {formatBytes(fileSize)}
          </div>
        </div>
      </div>
      <div className="flex gap-4 items-center">
        <div className="text-tag-text-three text-sm leading-3">
          {progress ? `${progress}%` : ''}
        </div>
        {canCancel && (
          <button
            type="button"
            className="w-fit h-fit p-1 rounded-full"
            onClick={event => {
              event.stopPropagation();
              handleRecordingFile(null);
              handleUploadedFile(null);
              setFile(null);
              setSuccess('');
            }}
          >
            <CrossIcon className="w-4 h-4 text-tag-text-three fill-current" />
          </button>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
